/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AnalyzeBankStatementsPayload } from '../models/AnalyzeBankStatementsPayload';
import type { AssetAccountBalanceCreate } from '../models/AssetAccountBalanceCreate';
import type { AssetAccountBalanceRead } from '../models/AssetAccountBalanceRead';
import type { AssetAccountDocumentCreatePayload } from '../models/AssetAccountDocumentCreatePayload';
import type { AssetAccountDocumentRead } from '../models/AssetAccountDocumentRead';
import type { AssetAccountDocumentUpdatePayload } from '../models/AssetAccountDocumentUpdatePayload';
import type { AssetAccountUpdate } from '../models/AssetAccountUpdate';
import type { AssetAccountWithDetails } from '../models/AssetAccountWithDetails';
import type { AssetAccountWithInstitutionConnection } from '../models/AssetAccountWithInstitutionConnection';
import type { BalanceSourceTypeEnum } from '../models/BalanceSourceTypeEnum';
import type { EntityFiltersPayload } from '../models/EntityFiltersPayload';
import type { FinancialInstitutionCreate } from '../models/FinancialInstitutionCreate';
import type { FinancialInstitutionRead } from '../models/FinancialInstitutionRead';
import type { FinancialInstitutionWithAssetAccounts } from '../models/FinancialInstitutionWithAssetAccounts';
import type { PaginatedResponse } from '../models/PaginatedResponse';
import type { UpdateAssetAccountsPayload } from '../models/UpdateAssetAccountsPayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AssetAccountsService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Create Or Update Asset Account Balance
     * @param assetAccountId
     * @param requestBody
     * @returns AssetAccountBalanceRead Successful Response
     * @throws ApiError
     */
    public createOrUpdateAssetAccountBalance(
        assetAccountId: string,
        requestBody: AssetAccountBalanceCreate,
    ): CancelablePromise<AssetAccountBalanceRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/asset-accounts/create-or-update-asset-account-balance/{asset_account_id}',
            path: {
                'asset_account_id': assetAccountId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Save Asset Account
     * @param assetAccountId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public saveAssetAccount(
        assetAccountId: string,
        requestBody: AssetAccountUpdate,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/asset-accounts/save-asset-account/{asset_account_id}',
            path: {
                'asset_account_id': assetAccountId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Get Asset Account
     * @param assetAccountId
     * @returns AssetAccountWithDetails Successful Response
     * @throws ApiError
     */
    public getAssetAccount(
        assetAccountId: string,
    ): CancelablePromise<AssetAccountWithDetails> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/asset-accounts/get-asset-account/{asset_account_id}',
            path: {
                'asset_account_id': assetAccountId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Financial Institutions Paginated
     * @param search
     * @returns PaginatedResponse Successful Response
     * @throws ApiError
     */
    public listFinancialInstitutionsPaginated(
        search: string,
    ): CancelablePromise<PaginatedResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/asset-accounts/list-financial-institutions-paginated',
            query: {
                'search': search,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Institution
     * @param requestBody
     * @returns FinancialInstitutionRead Successful Response
     * @throws ApiError
     */
    public createInstitution(
        requestBody: FinancialInstitutionCreate,
    ): CancelablePromise<FinancialInstitutionRead> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/asset-accounts/create-institution',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Institutions With Asset Accounts
     * @param entityId
     * @param ignoreArchived
     * @param asOf
     * @param sourceType
     * @returns FinancialInstitutionWithAssetAccounts Successful Response
     * @throws ApiError
     */
    public listInstitutionsWithAssetAccounts(
        entityId: string,
        ignoreArchived?: (boolean | null),
        asOf?: (string | null),
        sourceType?: (BalanceSourceTypeEnum | null),
    ): CancelablePromise<Array<FinancialInstitutionWithAssetAccounts>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/asset-accounts/list-institutions-with-asset-accounts',
            path: {
                'entity_id': entityId,
            },
            query: {
                'ignore_archived': ignoreArchived,
                'as_of': asOf,
                'source_type': sourceType,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Asset Accounts With Institution Connection Status
     * has nothing to do w/ balances - simply get asset accounts and institution connection data
     * @param entityId
     * @returns AssetAccountWithInstitutionConnection Successful Response
     * @throws ApiError
     */
    public listAssetAccountsWithInstitutionConnectionStatus(
        entityId: string,
    ): CancelablePromise<Array<AssetAccountWithInstitutionConnection>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/asset-accounts/list_asset_accounts_with_institution_connection_status',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Asset Documents For Institution
     * @param entityId
     * @returns FinancialInstitutionWithAssetAccounts Successful Response
     * @throws ApiError
     */
    public listAssetDocumentsForInstitution(
        entityId: string,
    ): CancelablePromise<Array<FinancialInstitutionWithAssetAccounts>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/asset-accounts/list-asset-documents-for-institution',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Asset Account Balances For Date
     * @param entityId
     * @param asOfDate
     * @returns AssetAccountBalanceRead Successful Response
     * @throws ApiError
     */
    public listAssetAccountBalancesForDate(
        entityId: string,
        asOfDate: string,
    ): CancelablePromise<Array<AssetAccountBalanceRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/asset-accounts/list-asset-account-balances-for-date/{as_of_date}',
            path: {
                'entity_id': entityId,
                'as_of_date': asOfDate,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Asset Account Balances Over Time
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public listAssetAccountBalancesOverTime(
        entityId: string,
    ): CancelablePromise<Array<Record<string, any>>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/asset-accounts/list-asset-account-balances-over-time',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Asset Account Documents
     * @param entityId
     * @param institutionId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public createAssetAccountDocuments(
        entityId: string,
        institutionId: string,
        requestBody: AssetAccountDocumentCreatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/asset-accounts/create-asset-account-documents/{institution_id}',
            path: {
                'entity_id': entityId,
                'institution_id': institutionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Asset Account Documents
     * @param entityId
     * @param institutionId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateAssetAccountDocuments(
        entityId: string,
        institutionId: string,
        requestBody: AssetAccountDocumentUpdatePayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/asset-accounts/update-asset-account-documents/{institution_id}',
            path: {
                'entity_id': entityId,
                'institution_id': institutionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Asset Accounts For Financial Connection
     * @param entityId
     * @param financialConnectionId
     * @returns AssetAccountWithDetails Successful Response
     * @throws ApiError
     */
    public listAssetAccountsForFinancialConnection(
        entityId: string,
        financialConnectionId: string,
    ): CancelablePromise<Array<AssetAccountWithDetails>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/entities/{entity_id}/asset-accounts/list-asset-accounts-for-financial-connection/{financial_connection_id}',
            path: {
                'entity_id': entityId,
                'financial_connection_id': financialConnectionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Update Asset Accounts
     * Used in the post-connect from plaid workflow
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public updateAssetAccounts(
        entityId: string,
        requestBody: UpdateAssetAccountsPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'PATCH',
            url: '/entities/{entity_id}/asset-accounts/update-asset-accounts',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Connect Token
     * @param entityId
     * @returns any Successful Response
     * @throws ApiError
     */
    public createConnectToken(
        entityId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/asset-accounts/create-connect-token',
            path: {
                'entity_id': entityId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Create Connect Token For Repair
     * @param entityId
     * @param localConnectionId
     * @returns any Successful Response
     * @throws ApiError
     */
    public createConnectTokenForRepair(
        entityId: string,
        localConnectionId: string,
    ): CancelablePromise<Record<string, any>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/asset-accounts/create-connect-token-for-repair',
            path: {
                'entity_id': entityId,
            },
            query: {
                'local_connection_id': localConnectionId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Asset Account Documents
     * @param entityId
     * @param documentId
     * @returns any Successful Response
     * @throws ApiError
     */
    public deleteAssetAccountDocuments(
        entityId: string,
        documentId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/asset-accounts/delete-asset-account-documents',
            path: {
                'entity_id': entityId,
            },
            query: {
                'document_id': documentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Analyze Bank Document
     * Kick off bank document analysis as a background task.
     *
     * Sends the document to Gemini directly.
     * Results are delivered via WebSocket on channel bank_document_analysis:{entity_id}.
     * @param entityId
     * @param documentId
     * @returns any Successful Response
     * @throws ApiError
     */
    public analyzeBankDocument(
        entityId: string,
        documentId: string,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/asset-accounts/analyze-bank-document',
            path: {
                'entity_id': entityId,
            },
            query: {
                'document_id': documentId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Analyze Bank Statements
     * Kick off Gemini analysis of uploaded bank statements (one task per file).
     *
     * Each statement yields every account and ending balance it contains; results are delivered via
     * WebSocket on channel bank_statement_analysis:{entity_id} so the Financial Accounts task block
     * can fill the balance of every matched account, not just one. Tasks are enqueued with a
     * staggered delay because simultaneous Gemini calls for a multi-statement batch exhaust the
     * Vertex AI per-minute quota (429 RESOURCE_EXHAUSTED).
     * @param entityId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    public analyzeBankStatements(
        entityId: string,
        requestBody: AnalyzeBankStatementsPayload,
    ): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/entities/{entity_id}/asset-accounts/analyze-bank-statements',
            path: {
                'entity_id': entityId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Institutions With Asset Accounts By Entity Id
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns FinancialInstitutionWithAssetAccounts Successful Response
     * @throws ApiError
     */
    public listInstitutionsWithAssetAccountsByEntityId(
        clusterId: string,
        namespaceId: string,
        requestBody: EntityFiltersPayload,
    ): CancelablePromise<Record<string, Array<FinancialInstitutionWithAssetAccounts>>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/asset-accounts/list-institutions-with-asset-accounts-by-entity-id',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Export Balances For Cluster
     * @param clusterId
     * @param namespaceId
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    public exportBalancesForCluster(
        clusterId: string,
        namespaceId: string,
        requestBody: EntityFiltersPayload,
    ): CancelablePromise<Record<string, string>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/asset-accounts/export-balances-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            query: {
                'namespace_id': namespaceId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Asset Account Documents For Cluster
     * @param clusterId
     * @returns AssetAccountDocumentRead Successful Response
     * @throws ApiError
     */
    public listAssetAccountDocumentsForCluster(
        clusterId: string,
    ): CancelablePromise<Array<AssetAccountDocumentRead>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/clusters/{cluster_id}/asset-accounts/list-asset-account-documents-for-cluster',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
